import { DATE_FORMATS, formatDate } from '@frontend/mwm-ui'

import type {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IAppEvent } from '~/types/event'
import type { IUserPublic } from '~/types/user'

import ActionMenu from '~/components/ActionMenu'
import { EventTypeBadge } from '~/components/Badges'
import { CreatedByCellRenderer } from '~/components/CellRenderers/CreatedByCellRenderer'
import { APP_EVENTS } from '~/types/event'
import { usersComparator } from '~/utils/comparators'
import { actionsColDef } from '~/utils/table'

export interface IContext {
  handleDeleteEvent: ({ id }: IAppEvent) => void
  handleEditEvent: ({ id }: IAppEvent) => void
}

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<IAppEvent, unknown, IContext>): ReactElement => {
  if (data) {
    const { handleDeleteEvent, handleEditEvent } = context

    const menu: Menu = {
      actions: [
        {
          action: () => handleDeleteEvent(data),
          label: 'Delete',
        },
        {
          action: () => handleEditEvent(data),
          label: 'Edit',
        },
      ],
    }

    return <ActionMenu menu={menu} placement="top" />
  }

  return <div />
}

const CustomCreatedByCellRenderer = (params: ICellRendererParams): ReactElement => {
  return params.data?.created_by ? <CreatedByCellRenderer {...params} /> : <div>Back-End</div>
}

const EventTypeCellRenderer = ({ value }: ICellRendererParams<IAppEvent, IAppEvent['event_type']>): ReactElement => {
  return <div>{value ? <EventTypeBadge eventType={value} /> : null}</div>
}

const COLUMNS: Record<string, keyof IAppEvent> = {
  CREATED_BY: 'created_by',
  DATE: 'date',
  EVENT_TYPE: 'event_type',
  VALUE: 'value',
}

const columns: ColDef[] = [
  {
    cellRenderer: EventTypeCellRenderer,
    field: COLUMNS.EVENT_TYPE,
    getQuickFilterText: ({ data }: GetQuickFilterTextParams<IAppEvent>): string => {
      return APP_EVENTS[data.event_type]?.label || data.event_type
    },
    headerName: 'Event type',
    suppressSizeToFit: true,
    width: 300,
  },
  {
    field: COLUMNS.VALUE,
    headerName: 'Message',
  },
  {
    cellRenderer: CustomCreatedByCellRenderer,
    comparator: usersComparator,
    field: COLUMNS.CREATED_BY,
    getQuickFilterText: (params: GetQuickFilterTextParams<IAppEvent & { created_by?: IUserPublic }>): string => {
      return params.data.created_by?.name
    },
    headerName: 'Created by',
    valueGetter: ({ data }: ValueGetterParams<IAppEvent>): Record<string, number | string> | undefined => {
      return data
        ? {
            created_at: data.created_at,
            created_by: data.created_by,
          }
        : undefined
    },
  },
  {
    field: COLUMNS.DATE,
    headerName: 'Event date',
    suppressSizeToFit: true,
    valueFormatter: ({ value }: ValueFormatterParams<IAppEvent>): string => {
      return formatDate(value, DATE_FORMATS.DEFAULT_WITH_HOURS)
    },
    width: 250,
  },
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
